import React from 'react';
import {
  Layout,
  Code,
  FileText,
  Package,
  Folder,
  Component,
  Layers,
  Settings,
  CheckCircle,
  GitBranch
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const Frontend = () => {
  const projectStructure = [
    {
      directory: "src/pages/",
      description: "Une page = un fichier .js complet",
      contents: [
        "Login.js - Page de connexion complète",
        "Dashboard.js - Interface principale",
        "UserProfile.js - Gestion profil",
        "Settings.js - Configuration utilisateur"
      ]
    },
    {
      directory: "src/shared/",
      description: "Éléments réutilisables simples",
      contents: [
        "Card.js - Wrapper design simple",
        "Button.js - Bouton standard",
        "Table.js - Tableau de données",
        "Modal.js - Fenêtre modale"
      ]
    },
    {
      directory: "src/services/",
      description: "Logique métier et API",
      contents: [
        "api.js - Appels API centralisés",
        "auth.js - Gestion authentification",
        "utils.js - Fonctions utilitaires"
      ]
    },
    {
      directory: "src/assets/",
      description: "Ressources statiques",
      contents: [
        "images/ - Images du projet",
        "icons/ - Icônes personnalisées"
      ]
    }
  ];

  const pageTemplate = {
    structure: [
      {
        section: "Imports",
        content: [
          "React et hooks nécessaires",
          "Components partagés",
          "Services requis",
          "Assets utilisés"
        ]
      },
      {
        section: "Composant Page",
        content: [
          "État local avec useState",
          "Effets avec useEffect",
          "Gestionnaires d'événements",
          "Rendu JSX complet"
        ]
      },
      {
        section: "Styles",
        content: [
          "Classes Tailwind intégrées",
          "Styles inline si nécessaire"
        ]
      }
    ]
  };

  const bestPractices = [
    {
      category: "Organisation",
      practices: [
        "Une page = un fichier unique et complet",
        "Composants intégrés dans la page",
        "Styles Tailwind directement dans le JSX",
        "Logique métier dans le composant"
      ]
    },
    {
      category: "Performance",
      practices: [
        "Lazy loading des pages",
        "Images optimisées",
        "État local minimal",
        "Requêtes API optimisées"
      ]
    },
    {
      category: "Code",
      practices: [
        "Nommage explicite des composants",
        "Commentaires sur les sections complexes",
        "Destructuration des props",
        "Gestion d'erreurs intégrée"
      ]
    }
  ];

  const examplePage = `// UserProfile.js
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import Card from '../shared/Card';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const data = await api.getUser();
      setUser(data);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Logique de mise à jour
  };

  return (
    <div className="p-6">
      <Card>
        {/* Contenu complet */}
      </Card>
    </div>
  );
};

export default UserProfile;`;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <h2 className="text-2xl font-bold text-center text-white">
            Architecture Frontend React
          </h2>
          <p className="text-center text-white mt-2 opacity-90">
            Guide de construction simple et efficace
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Structure du Projet */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Folder className="mr-2" />
              Structure du Projet
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projectStructure.map((dir, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-2">{dir.directory}</h4>
                  <p className="text-sm text-gray-600 mb-3">{dir.description}</p>
                  <ul className="space-y-2">
                    {dir.contents.map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <FileText className="w-4 h-4 mr-2 text-blue-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Template de Page */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Layout className="mr-2" />
              Structure d'une Page
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-blue-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {pageTemplate.structure.map((section, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg shadow">
                    <h4 className="font-medium text-blue-600 mb-3">{section.section}</h4>
                    <ul className="space-y-2">
                      {section.content.map((item, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 mt-1 text-green-500" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Best Practices */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Settings className="mr-2" />
              Meilleures Pratiques
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {bestPractices.map((category, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{category.category}</h4>
                  <ul className="space-y-2">
                    {category.practices.map((practice, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 mt-1 text-green-500" />
                        {practice}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Exemple de Code */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Code className="mr-2" />
              Exemple de Page
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-blue-200">
              <pre className="text-sm text-gray-800 overflow-x-auto">
                <code>{examplePage}</code>
              </pre>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Frontend;